import React, { useState, useEffect } from 'react';
import { Stack, Paper, Text, Loader, Center } from '@mantine/core';
import {
  PaymentBookingsResponseModel,
  PaymentBookingResponseModel,
} from '../types/PaymentModels';
import {
  getProfessionalPaymentBookingsByMonth,
  getUserPaymentBookingsByMonth,
} from '../services/PaymentService';
import { PaymentHistoryCell } from '../components/PaymentHistoryCell';
import { primaryColor } from '../constants/theme';

export type PaymentHistoryProps = {
  transactionType: 'Received' | 'Sent';
  year: string;
  month: string;
};

export const PaymentHistoryList: React.FC<PaymentHistoryProps> = ({
  transactionType,
  year,
  month,
}: PaymentHistoryProps) => {
  const [paymentBookings, setPaymentBookings] = useState<
    PaymentBookingResponseModel[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token') || '';
        let response: PaymentBookingsResponseModel;
        const formattedMonth = `${year}-${String(new Date(`${month} 1, ${year}`).getMonth() + 1).padStart(2, '0')}`;
        if (transactionType === 'Received') {
          response = await getProfessionalPaymentBookingsByMonth(
            formattedMonth,
            token
          );
        } else {
          response = await getUserPaymentBookingsByMonth(formattedMonth, token);
        }
        setPaymentBookings(response.paymentBookings);
      } catch (error) {
        console.error('Error fetching payment bookings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [transactionType, year, month]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
      })
      .toUpperCase();
  };

  const pendingBookings = paymentBookings.filter(
    (booking) => booking.paymentStatus === 'pending'
  );
  const completedBookings = paymentBookings.filter(
    (booking) => booking.paymentStatus !== 'pending'
  );

  return (
    <Stack mt={15} data-testid="payment-history-list">
      {isLoading ? (
        <Center py="xl" data-testid="payment-history-list-loading">
          <Loader color={primaryColor} />
        </Center>
      ) : paymentBookings.length === 0 ? (
        <Text mt="sm" mb="sm" data-testid="payment-history-list-empty">
          No payments {transactionType.toLowerCase()} in{' '}
          <Text component="span" fw={500}>
            {month} {year}
          </Text>
        </Text>
      ) : (
        <Stack gap="sm">
          {pendingBookings.length > 0 && (
            <>
              <Text
                size="xs"
                fw={700}
                tt="uppercase"
                c="dimmed"
                data-testid="payment-history-list-pending-label"
              >
                Pending
              </Text>
              {pendingBookings.map((booking) => (
                <Paper
                  key={booking.id}
                  withBorder
                  radius="md"
                  style={{ overflow: 'hidden' }}
                  data-testid={`payment-history-booking-${booking.id}`}
                >
                  <PaymentHistoryCell
                    booking={booking}
                    transactionType={transactionType}
                  />
                </Paper>
              ))}
            </>
          )}
          {completedBookings.map((booking, i) => (
            <React.Fragment key={booking.id}>
              {(i === 0 ||
                formatDate(completedBookings[i - 1].startTime) !==
                  formatDate(booking.startTime)) && (
                <Text
                  size="xs"
                  fw={500}
                  tt="uppercase"
                  c="dimmed"
                  mt={i === 0 ? 0 : 'xs'}
                >
                  {formatDate(booking.startTime)}
                </Text>
              )}
              <Paper
                withBorder
                radius="md"
                style={{ overflow: 'hidden' }}
                data-testid={`payment-history-booking-${booking.id}`}
              >
                <PaymentHistoryCell
                  booking={booking}
                  transactionType={transactionType}
                />
              </Paper>
            </React.Fragment>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export const CenteredLoader = () => (
  <Center py="xl">
    <Loader />
  </Center>
);
